import { ApiResponse, RequestOptions, useApi } from "@kamae-apps/shared/Hooks/useApi"
import type { APIResponse } from "@kamae-apps/shared/Types/ApiResponse"
import { SCOPE } from "@kamae-apps/shared/Types/Scope"
import type User from "@kamae-apps/shared/Types/User"
import { NewUser, USER_STATUS } from "@kamae-apps/shared/Types/User"
import { apiRequest, apiRequestRaw } from "@kamae-apps/shared/utils"
import { BatchResult } from "../Components/BatchErrorModal/type"

export type UpdateUserPayload = {
  email: string
  name: string
  first_name: string
  team: number
  status?: USER_STATUS
  scope?: SCOPE
}

class UserService {
  fetchUserData(): Promise<User> {
    try {
      return apiRequest<User>("/user?v2=true", { name: "v2" })
    } catch (error) {
      throw new Error(error)
    }
  }

  fetchUserV1Data(): Promise<User> {
    try {
      return apiRequest<User>("/user", { name: "v2" })
    } catch (error) {
      throw new Error(error)
    }
  }

  updateUserData(body: User): Promise<User> {
    try {
      return apiRequest("/user?v2=true", {
        name: "v2",
        method: "PUT",
        body,
      })
    } catch (error) {
      throw new Error(error)
    }
  }

  importUserBatch(csvContent: ArrayBuffer | string | null, languageId: number): Promise<BatchResult> {
    return apiRequestRaw(`/users/batch?languageId=${languageId}`, "text/csv; charset=utf-8", {
      method: "POST",
      body: csvContent,
    }) as Promise<BatchResult>
  }

  getAuthMethod(email: string): Promise<APIResponse<string>> {
    return apiRequest<APIResponse<string>>(`/user/auth?mail=${email.toLowerCase()}`)
  }

  updateUserLanguage(code: string): Promise<unknown> {
    return apiRequest("/user/lang", {
      method: "POST",
      body: { code },
      headers: { "X-Targeted-Language": code },
    })
  }

  createUser(data: NewUser): Promise<User> {
    return apiRequest<User>("/user", { method: "POST", body: data })
  }

  updateUser(userId: number, data: UpdateUserPayload): Promise<User> {
    return apiRequest<User>(`/user/${userId}`, { method: "PUT", body: data })
  }

  deleteUser(userId: number): Promise<void> {
    return apiRequest<void>(`/user/${userId}`, { method: "DELETE" })
  }

  getAdminCount(): ApiResponse<number> {
    return useApi<number>("/companies/admin-count", {})
  }

  getSupervisionUsers(requestOptions?: RequestOptions): ApiResponse<User[]> {
    return useApi<User[]>("/supervision/users", requestOptions ?? {})
  }

  getSupervisionUsersCount(requestOptions?: RequestOptions): ApiResponse<number> {
    return useApi<number>("/supervision/users/count", requestOptions ?? {})
  }
}

export const userService = new UserService()
